import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, Plus, Trash2, Save, RefreshCcw, GraduationCap, BookOpen, ListChecks, Settings, UserCheck, Award, Upload } from "lucide-react";
const TABS = [
  { key: "bank", label: "Question Bank", icon: BookOpen },
  { key: "configs", label: "Exam Configurations", icon: Settings },
  { key: "attempts", label: "Student Attempts", icon: ListChecks },
  { key: "overrides", label: "Instructor Overrides", icon: UserCheck },
  { key: "dts", label: "DTS Approvals", icon: Award },
];

const LEVELS = [
  { key: "scalp_scientist", label: "Certified Scalp Scientist" },
  { key: "associate_trichologist", label: "Associate Trichologist" },
  { key: "holistic_trichology_practitioner", label: "Holistic Trichology Practitioner" },
  { key: "clinical_residency", label: "Clinical Trichology Residency" },
  { key: "herbal_medicine_practitioner", label: "Herbal Medicine Practitioner" },
  { key: "functional_health_practitioner", label: "Functional Health Practitioner" },
  { key: "holistic_wellness_coach", label: "Holistic Wellness Coach" },
  { key: "dts", label: "Doctor of Trichology Science" },
];

export default function AdminUniversityExams() {
  const [tab, setTab] = useState("bank");
  return (
    <AdminShell door="university" title="Final Exams">
      <div data-testid="admin-exams-page">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 ${tab === t.key ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-purple-300"}`}
              data-testid={`admin-exams-tab-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
          <Link
            to="/admin/university/exams/bulk-upload"
            className="ml-auto text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="admin-exams-bulk-upload-link"
          >
            <Upload className="w-3.5 h-3.5" />Bulk Upload Questions
          </Link>
        </div>
        {tab === "bank" && <QuestionBank />}
        {tab === "configs" && <ExamConfigs />}
        {tab === "attempts" && <Attempts />}
        {tab === "overrides" && <Overrides />}
        {tab === "dts" && <DtsApprovals />}
      </div>
    </AdminShell>
  );
}

function QuestionBank() {
  const [program, setProgram] = useState("scalp_scientist");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await universityApi.examQuestionsList({ program_id: program });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load questions"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [program]);

  const blank = {
    program_id: program, course_id: "", question_type: "multiple_choice",
    difficulty: "medium", tags: [], competency_area: "",
    question_text: "", answer_options: [{ label: "Option A" }, { label: "Option B" }, { label: "Option C" }, { label: "Option D" }],
    correct_answer: 0, required_keywords: [], explanation: "", active: true,
  };

  const remove = async (id) => {
    if (!confirm("Delete this question?")) return;
    try { await universityApi.examQuestionDelete(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wide text-slate-500">Program</span>
          <select value={program} onChange={(e) => setProgram(e.target.value)} className="rounded-xl border border-slate-200 h-9 px-2 text-sm" data-testid="admin-exam-program">
            {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" />Reload</button>
        </div>
        <button onClick={() => setEditing(blank)} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="admin-exam-new-question"><Plus className="w-4 h-4" />New question</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-4 text-xs text-slate-600">
        <b className="text-slate-900">{items.length}</b> questions in bank · active: <b className="text-slate-900">{items.filter(q => q.active).length}</b>
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          No questions in this program&apos;s bank yet. Click &ldquo;New question&rdquo; to add the first one.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((q) => (
            <li key={q.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-3" data-testid={`admin-exam-q-${q.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-slate-900 truncate">{q.question_text}</div>
                <div className="text-[11px] text-slate-500">
                  {q.question_type?.replace(/_/g, " ")} · {q.difficulty}
                  {q.course_id ? ` · ${q.course_id}` : ""}
                  {q.active ? "" : " · inactive"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(q)} className="text-xs text-purple-900 hover:underline">Edit</button>
                <button onClick={() => remove(q.id)} className="text-xs text-rose-600 hover:underline"><Trash2 className="w-3 h-3" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && <QuestionForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </section>
  );
}

function QuestionForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.question_text.trim()) return toast.error("Question text required");
    setSaving(true);
    try {
      // Normalize answer options + correct_answer based on type
      const body = { ...form };
      if (form.question_type === "multiple_choice" || form.question_type === "true_false") {
        body.correct_answer = Number(form.correct_answer) || 0;
      } else if (form.question_type === "multiple_answer") {
        body.correct_answer = Array.isArray(form.correct_answer) ? form.correct_answer.map(Number) : [];
      } else if (form.question_type === "matching") {
        body.correct_answer = typeof form.correct_answer === "object" ? form.correct_answer : {};
      } else if (form.question_type === "short_answer") {
        body.correct_answer = String(form.correct_answer || "");
      } else {
        body.correct_answer = String(form.correct_answer || "");
      }
      if (form.id) {
        await universityApi.examQuestionPatch(form.id, body);
        toast.success("Question updated");
      } else {
        await universityApi.examQuestionCreate(body);
        toast.success("Question created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-purple-200 bg-purple-50/30 p-5" data-testid="admin-exam-form">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">{form.id ? "Edit question" : "New question"}</div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <select value={form.question_type} onChange={(e) => set("question_type", e.target.value)} className="md:col-span-3 rounded-xl border border-slate-200 h-9 px-2 text-sm" data-testid="admin-exam-q-type">
          <option value="multiple_choice">Multiple Choice</option>
          <option value="true_false">True / False</option>
          <option value="multiple_answer">Multiple Answer</option>
          <option value="matching">Matching</option>
          <option value="short_answer">Short Answer</option>
          <option value="case_analysis">Case Analysis</option>
          <option value="scenario_based">Scenario-Based</option>
        </select>
        <select value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="md:col-span-2 rounded-xl border border-slate-200 h-9 px-2 text-sm">
          <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
        </select>
        <input value={form.course_id} onChange={(e) => set("course_id", e.target.value)} placeholder="Course (optional, e.g. TRI-101)" className="md:col-span-3 rounded-xl border border-slate-200 h-9 px-2 text-sm font-mono" />
        <input value={form.competency_area} onChange={(e) => set("competency_area", e.target.value)} placeholder="Competency area" className="md:col-span-4 rounded-xl border border-slate-200 h-9 px-2 text-sm" />
      </div>
      <textarea value={form.question_text} onChange={(e) => set("question_text", e.target.value)} rows={3} placeholder="Question text" className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="admin-exam-q-text" />

      <div className="mt-3">
        {(form.question_type === "multiple_choice" || form.question_type === "true_false" || form.question_type === "multiple_answer") && (
          <McOptionsEditor form={form} setForm={setForm} />
        )}
        {form.question_type === "matching" && <MatchingEditor form={form} setForm={setForm} />}
        {form.question_type === "short_answer" && (
          <input value={form.correct_answer || ""} onChange={(e) => set("correct_answer", e.target.value)} placeholder="Correct answer (case-insensitive)" className="w-full rounded-xl border border-slate-200 px-3 h-9 text-sm" />
        )}
        {(form.question_type === "case_analysis" || form.question_type === "scenario_based") && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Required keywords (auto-assist; ≥60% match = provisional pass)</div>
            <input
              value={(form.required_keywords || []).join(", ")}
              onChange={(e) => set("required_keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="keyword1, keyword2, keyword3"
              className="w-full rounded-xl border border-slate-200 px-3 h-9 text-sm"
              data-testid="admin-exam-keywords"
            />
            <div className="text-[11px] text-slate-500 mt-1">Instructor will be flagged for review. Instructor may override the grade.</div>
          </div>
        )}
      </div>

      <textarea value={form.explanation} onChange={(e) => set("explanation", e.target.value)} rows={2} placeholder="Explanation (shown only when admin enables answer review)" className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />

      <div className="mt-3 flex items-center gap-3 text-xs">
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />Active</label>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="text-xs text-slate-500 hover:underline">Cancel</button>
        <button onClick={save} disabled={saving} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="admin-exam-q-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{form.id ? "Save" : "Create question"}
        </button>
      </div>
    </div>
  );
}

function McOptionsEditor({ form, setForm }) {
  const opts = form.answer_options || [];
  const isMulti = form.question_type === "multiple_answer";
  const correct = form.correct_answer;
  const arr = isMulti ? (Array.isArray(correct) ? correct.map(Number) : []) : [];
  const setOpts = (next) => setForm((s) => ({ ...s, answer_options: next }));
  const setLabel = (i, v) => setOpts(opts.map((o, idx) => idx === i ? { ...o, label: v } : o));
  const add = () => setOpts([...opts, { label: `Option ${String.fromCharCode(65 + opts.length)}` }]);
  const remove = (i) => setOpts(opts.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">Options · mark correct {isMulti ? "(check all that apply)" : "(select one)"}</div>
      {opts.map((o, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <div className="col-span-1 flex items-center justify-center">
            {isMulti ? (
              <input type="checkbox" checked={arr.includes(i)} onChange={() => {
                const next = arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i];
                setForm((s) => ({ ...s, correct_answer: next }));
              }} />
            ) : (
              <input type="radio" name="mc-correct" checked={Number(correct) === i} onChange={() => setForm((s) => ({ ...s, correct_answer: i }))} />
            )}
          </div>
          <input className="col-span-10 rounded-xl border border-slate-200 px-3 h-9 text-sm" value={o.label || ""} onChange={(e) => setLabel(i, e.target.value)} />
          <button onClick={() => remove(i)} className="col-span-1 text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={add} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add option</button>
    </div>
  );
}

function MatchingEditor({ form, setForm }) {
  const opts = form.answer_options || [];
  const correct = form.correct_answer || {};
  const setOpts = (next) => setForm((s) => ({ ...s, answer_options: next }));
  const setLeft = (i, v) => {
    const next = opts.map((p, idx) => idx === i ? { ...p, left: v } : p);
    setOpts(next);
  };
  const setRights = (i, list) => setOpts(opts.map((p, idx) => idx === i ? { ...p, right_options: list } : p));
  const setCorrect = (left, right) => setForm((s) => ({ ...s, correct_answer: { ...(s.correct_answer || {}), [left]: right } }));
  const add = () => setOpts([...opts, { left: "", right_options: [""] }]);
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">Matching pairs (left column · right options · correct match)</div>
      {opts.map((pair, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <input className="col-span-4 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Left item" value={pair.left || ""} onChange={(e) => setLeft(i, e.target.value)} />
          <input className="col-span-5 rounded-xl border border-slate-200 px-3 h-9 text-sm" placeholder="Right options (comma-separated)" value={(pair.right_options || []).join(", ")} onChange={(e) => setRights(i, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
          <input className="col-span-3 rounded-xl border border-purple-200 px-3 h-9 text-sm" placeholder="Correct match" value={correct[pair.left] || ""} onChange={(e) => setCorrect(pair.left, e.target.value)} />
        </div>
      ))}
      <button onClick={add} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add pair</button>
    </div>
  );
}

function ExamConfigs() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await universityApi.examConfigsList();
      setConfigs(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load configs"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    try {
      const r = await universityApi.examConfigsSeedDefaults();
      toast.success(`Seeded ${r.data.created} configs`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Seed failed")); }
  };

  return (
    <section data-testid="admin-exams-configs">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">Per-program final exam configurations</div>
        <button onClick={seed} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" />Seed defaults for all programs</button>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="space-y-3">
          {configs.map((c) => <ConfigRow key={c.level_key} cfg={c} reload={load} />)}
        </div>
      )}
    </section>
  );
}

function ConfigRow({ cfg, reload }) {
  const [form, setForm] = useState(cfg);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await universityApi.examConfigUpsert(form.level_key, {
        level_key: form.level_key,
        passing_score: Number(form.passing_score),
        max_attempts: Number(form.max_attempts),
        default_questions: Number(form.default_questions),
        time_limit_minutes: Number(form.time_limit_minutes),
        waiting_period_hours: Number(form.waiting_period_hours),
        show_answer_review: !!form.show_answer_review,
        required: !!form.required,
      });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setSaving(false); }
  };
  const label = LEVELS.find((l) => l.key === form.level_key)?.label || form.level_key;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-exam-config-${form.level_key}`}>
      <div className="text-sm font-medium text-slate-900 mb-3">{label} <span className="text-[11px] text-slate-500 font-mono ml-2">{form.level_key}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Field label="Passing %"><input type="number" value={form.passing_score} onChange={(e) => set("passing_score", e.target.value)} className="rounded-xl border border-slate-200 px-2 h-9 text-sm w-full" /></Field>
        <Field label="Attempts"><input type="number" value={form.max_attempts} onChange={(e) => set("max_attempts", e.target.value)} className="rounded-xl border border-slate-200 px-2 h-9 text-sm w-full" /></Field>
        <Field label="Questions"><input type="number" value={form.default_questions} onChange={(e) => set("default_questions", e.target.value)} className="rounded-xl border border-slate-200 px-2 h-9 text-sm w-full" /></Field>
        <Field label="Time (min)"><input type="number" value={form.time_limit_minutes} onChange={(e) => set("time_limit_minutes", e.target.value)} className="rounded-xl border border-slate-200 px-2 h-9 text-sm w-full" /></Field>
        <Field label="Wait (hr)"><input type="number" value={form.waiting_period_hours} onChange={(e) => set("waiting_period_hours", e.target.value)} className="rounded-xl border border-slate-200 px-2 h-9 text-sm w-full" /></Field>
        <Field label="Required"><input type="checkbox" checked={!!form.required} onChange={(e) => set("required", e.target.checked)} /></Field>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <label className="text-xs inline-flex items-center gap-1.5"><input type="checkbox" checked={!!form.show_answer_review} onChange={(e) => set("show_answer_review", e.target.checked)} />Show answer review after submission</label>
        <button onClick={save} disabled={saving} className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Attempts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ level_key: "", status: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.level_key) params.level_key = filter.level_key;
      if (filter.status) params.status = filter.status;
      const r = await universityApi.examAttemptsList(params);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <section data-testid="admin-exams-attempts">
      <div className="flex items-center gap-2 mb-3">
        <select value={filter.level_key} onChange={(e) => setFilter((s) => ({ ...s, level_key: e.target.value }))} className="rounded-xl border border-slate-200 h-9 px-2 text-sm">
          <option value="">All programs</option>
          {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter((s) => ({ ...s, status: e.target.value }))} className="rounded-xl border border-slate-200 h-9 px-2 text-sm">
          <option value="">All statuses</option>
          <option value="in_progress">In progress</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
          <option value="locked">Locked</option>
        </select>
        <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" />Reload</button>
      </div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : items.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No attempts in this view.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm flex items-center justify-between gap-3" data-testid={`admin-exam-attempt-${a.id}`}>
              <div className="min-w-0">
                <div className="text-slate-900 truncate">{a.student_email} · <span className="font-mono text-purple-900">{a.level_key}</span></div>
                <div className="text-[11px] text-slate-500">Attempt #{a.attempt_number} · {a.score}% · started {(a.started_at || "").slice(0, 19).replace("T", " ")}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] ${a.status === "passed" ? "bg-emerald-100 text-emerald-700" : a.status === "locked" ? "bg-rose-100 text-rose-700" : a.status === "failed" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"}`}>{a.status?.toUpperCase()}</span>
              {a.needs_review && <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-700">Needs review</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Overrides() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Pull locked, failed, and needs_review attempts
      const [a1, a2, a3] = await Promise.all([
        universityApi.examAttemptsList({ status: "locked" }),
        universityApi.examAttemptsList({ status: "failed" }),
        universityApi.examAttemptsList({ needs_review: true }),
      ]);
      const merged = new Map();
      [...(a1.data?.items || []), ...(a2.data?.items || []), ...(a3.data?.items || [])].forEach((x) => merged.set(x.id, x));
      setAttempts([...merged.values()]);
    } catch (e) {
      toast.error(formatApiError(e, "Load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const override = async (id, body) => {
    try {
      await universityApi.examAttemptOverride(id, body);
      toast.success("Override applied");
      load();
      setSelected(null);
    } catch (e) { toast.error(formatApiError(e, "Override failed")); }
  };

  return (
    <section data-testid="admin-exams-overrides">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Locked, failed, or needs-review attempts</div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : attempts.length === 0 ? (
        <div className="text-sm text-slate-500 italic">Nothing pending instructor review or unlock.</div>
      ) : (
        <ul className="space-y-2">
          {attempts.map((a) => (
            <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`admin-exam-override-${a.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-slate-900 truncate">{a.student_email} · <span className="font-mono text-purple-900">{a.level_key}</span></div>
                  <div className="text-[11px] text-slate-500">Attempt #{a.attempt_number} · {a.score}% · {a.status?.toUpperCase()}{a.needs_review ? " · NEEDS REVIEW" : ""}</div>
                </div>
                <button onClick={() => setSelected(selected === a.id ? null : a.id)} className="text-xs text-purple-900 hover:underline">{selected === a.id ? "Collapse" : "Open"}</button>
              </div>
              {selected === a.id && (
                <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <button onClick={() => override(a.id, { unlock: true })} className="rounded-xl bg-amber-700 hover:bg-amber-600 text-white h-9 text-sm" data-testid={`admin-exam-unlock-${a.id}`}>Unlock for retake</button>
                    <button onClick={() => override(a.id, { force_pass: true })} className="rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white h-9 text-sm" data-testid={`admin-exam-force-pass-${a.id}`}>Force PASS (instructor approves)</button>
                    <button onClick={() => {
                      const v = prompt("New score (0-100):", String(a.score || 0));
                      if (v == null) return;
                      override(a.id, { score: Number(v) });
                    }} className="rounded-xl border border-slate-200 h-9 text-sm">Override score</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DtsApprovals() {
  const [items, setItems] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        universityApi.dtsPortfolios(),
        universityApi.adminEnrollmentList({ q: "Doctor of Trichology Science" }),
      ]);
      setItems(p.data?.items || []);
      const enrs = e.data?.items || e.data || [];
      setEnrollments(Array.isArray(enrs) ? enrs : []);
    } catch (err) {
      toast.error(formatApiError(err, "Load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (enrollmentId, body) => {
    try {
      await universityApi.dtsApprove(enrollmentId, { ...body, approved_by: "admin" });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e, "Save failed")); }
  };

  // Merge enrollments + portfolios
  const merged = useMemo(() => {
    const byEnr = {};
    items.forEach((p) => { byEnr[p.enrollment_id] = p; });
    return enrollments.map((enr) => ({ enr, portfolio: byEnr[enr.id] || {} }));
  }, [enrollments, items]);

  return (
    <section data-testid="admin-exams-dts">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3 inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" />Doctor of Trichology Science — Portfolio · Dissertation · Defense</div>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : merged.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No DTS enrollments to approve.</div>
      ) : (
        <ul className="space-y-3">
          {merged.map(({ enr, portfolio }) => (
            <li key={enr.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-dts-${enr.id}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">{enr.student_name || enr.student_email}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{enr.student_email} · {enr.id}</div>
                </div>
                <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full ${enr.status === "graduated" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{enr.status}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Toggle label="Portfolio complete" value={!!portfolio.portfolio_complete} onChange={(v) => approve(enr.id, { portfolio_complete: v })} testid={`admin-dts-portfolio-${enr.id}`} />
                <Toggle label="Dissertation approved" value={!!portfolio.dissertation_approved} onChange={(v) => approve(enr.id, { dissertation_approved: v })} testid={`admin-dts-dissertation-${enr.id}`} />
                <Toggle label="Defense approved" value={!!portfolio.defense_approved} onChange={(v) => approve(enr.id, { defense_approved: v })} testid={`admin-dts-defense-${enr.id}`} />
              </div>
              {portfolio.approved_date && <div className="text-[11px] text-slate-500 mt-2">Last update: {portfolio.approved_date.slice(0, 19).replace("T", " ")} UTC</div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`text-left rounded-xl border p-3 ${value ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-purple-300"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm font-medium ${value ? "text-emerald-800" : "text-slate-700"}`}>{value ? "✓ Approved" : "Pending"}</div>
    </button>
  );
}
